<script setup lang="ts">
import { computed } from "vue";
import { useBreakpoint } from "@/design/composables/use-breakpoint";
import Icon from "@/design/primitives/Icon.vue";
import Tooltip from "@/design/primitives/Tooltip.vue";
import BenOrb, { type OrbState } from "@/shared/agent-display/BenOrb.vue";
import type { ChatAgent } from "../api/use-agents";
import type { AgentDoor } from "../api/use-ben";
import { useAgentsI18n } from "../i18n/use-agents-i18n";

/** One team assistant; historical agent identities remain in their conversations. */

const props = defineProps<{
	agent: ChatAgent;
	state: OrbState;
	listing: boolean;
	/** Onde esta doca está desenhada, e é o SERVIDOR quem disse (ver `use-ben.ts`). */
	door: AgentDoor;
	/** Esta superfície oferece a lista de conversas. */
	listable?: boolean;
	/**
	 * A janela está OCUPANDO A TELA.
	 *
	 * O modo de vista da V2 (`BenPanel` tem drawer, flutuante e tela cheia), na
	 * versão que a V3 realmente precisa: as respostas do Ben são tabelas, listas e
	 * código, e ler uma tabela de nove colunas numa gaveta de 30rem é o motivo pelo
	 * qual alguém copia a resposta para outro lugar. O flutuante arrastável ficou
	 * de fora — o split já resolve a largura, e três modos numa doca seriam três
	 * estados para o mesmo pedido.
	 */
	wide?: boolean;
	newInSidebar?: boolean;
	/** Esta superfície oferece o modo tela cheia. A coluna da conversa não. */
	widenable?: boolean;
	/**
	 * "Nova conversa" está À ESPERA DO SEGUNDO CLIQUE.
	 *
	 * Só acontece na porta da conversa, e por um motivo real: lá recomeçar ARQUIVA
	 * a thread desta conversa, porque há uma só por assunto e o servidor devolve
	 * sempre a mesma. Dois cliques e não um `confirm()`, que é bloqueado numa vista
	 * embebida e lê como aviso do browser em vez de decisão sobre esta conversa —
	 * o mesmo arranjo que `SessionList` já usa para apagar.
	 */
	armed?: boolean;
}>();

const visibleName = computed(() => {
	if (props.agent.code === "builder" && props.agent.mode === "builder") {
		return "Ben";
	}

	return props.agent.name;
});

const emit = defineEmits<{
	toggleList: [];
	startNew: [];
	toggleWide: [];
	close: [];
}>();

const { t } = useAgentsI18n();
const { isMobile } = useBreakpoint();

/** O quadrado comum aos quatro botões: alvo de 36px, o mínimo confortável ao toque. */
const BUTTON =
	"inline-flex size-9 items-center justify-center rounded-md transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent";

function statusOf(state: OrbState, door: AgentDoor): string {
	if (state === "acting") return t("dock.acting");
	if (state === "typing") return t("dock.typing");
	if (state === "thinking") return t("dock.thinking");
	return t(door === "conversation" ? "dock.purposeInConversation" : "dock.purposeInternal");
}
</script>

<template>
	<!-- No telefone, o arranjo do app do Claude: conversas à esquerda (onde mora o ☰),
	     o orbe no centro — rosto e estado do Ben num só sinal, sem disputar canto com
	     menu nenhum — e nova conversa + fechar numa pílula à direita. Tela cheia não
	     existe aqui: a gaveta já é a tela inteira. -->
	<header v-if="isMobile" class="absolute inset-x-0 top-0 z-10 flex items-center justify-between bg-gradient-to-b px-3 pt-2 pb-4" :class="door === 'conversation' ? 'from-warning-subtle via-warning-subtle/90 to-transparent' : 'from-surface via-surface/90 to-transparent'">
		<button
			v-if="listable"
			type="button"
			class="flex size-11 items-center justify-center rounded-full border border-border bg-bg-muted/80 text-fg backdrop-blur focus-visible:outline-2 focus-visible:outline-accent"
			:class="listing ? 'bg-accent-subtle text-accent' : ''"
			:aria-expanded="listing"
			:aria-label="t('dock.conversations')"
			@click="emit('toggleList')"
		>
			<Icon name="menu" class="size-5" />
		</button>
		<span v-else class="size-11" aria-hidden="true" />
		<!-- O NOME volta, com o orbe pequeno ao lado como o sinal de estado: sozinho
		     no meio, o orbe não dizia com quem se fala (dono, 25/10). -->
		<div class="absolute left-1/2 flex max-w-[45%] -translate-x-1/2 items-center gap-2" :title="statusOf(state, door)">
			<BenOrb :state="state" :size="22" :tone="door === 'conversation' ? 'copilot' : 'ben'" />
			<span class="truncate text-base font-semibold text-fg">{{ door === "conversation" ? t("dock.copilot") : visibleName }}</span>
			<span class="sr-only">· {{ statusOf(state, door) }}</span>
		</div>
		<div class="flex items-center rounded-full border border-border bg-bg-muted/80 backdrop-blur">
			<button
				v-if="!newInSidebar"
				type="button"
				class="flex size-11 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-accent"
				:class="armed ? 'bg-danger-subtle text-danger' : 'text-fg'"
				:aria-label="armed ? t('dock.confirmRestart') : t('dock.newConversation')"
				@click="emit('startNew')"
			>
				<Icon :name="armed ? 'check' : 'compose'" class="size-5" />
			</button>
			<button
				type="button"
				class="flex size-11 items-center justify-center rounded-full text-fg focus-visible:outline-2 focus-visible:outline-accent"
				:aria-label="t('dock.close')"
				@click="emit('close')"
			>
				<Icon name="close" class="size-5" />
			</button>
		</div>
	</header>
	<header v-else class="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3" :class="door === 'conversation' ? 'bg-warning-subtle' : ''">
		<BenOrb :state="state" :size="34" :tone="door === 'conversation' ? 'copilot' : 'ben'" />
		<div class="min-w-0 flex-1">
			<p class="truncate text-sm font-semibold text-fg">{{ door === "conversation" ? t("dock.copilot") : visibleName }}</p>

			<p class="truncate text-xs text-fg-subtle">{{ statusOf(state, door) }}</p>
		</div>
		<!-- Botões de 36px com traço de 20px, como a V2 (32/16) um degrau acima:
		     eram caracteres a 12px, cada um com a altura da sua fonte, e o × era
		     o menor alvo da doca justamente onde se clica mais. -->
		<div class="-mr-1.5 flex shrink-0 items-center gap-0.5">
			<Tooltip v-if="listable" :text="listing ? t('dock.backToChat') : t('dock.conversations')">
				<button
					type="button"
					:class="[BUTTON, listing ? 'bg-accent-subtle text-accent' : 'text-fg-subtle hover:bg-bg-muted hover:text-fg']"
					:aria-expanded="listing"
					:aria-label="t('dock.conversations')"
					@click="emit('toggleList')"
				>
					<Icon name="history" class="size-5" />
				</button>
			</Tooltip>
			<Tooltip v-if="widenable" :text="wide ? t('dock.narrow') : t('dock.wide')">
				<button
					type="button"
					:class="[BUTTON, 'text-fg-subtle hover:bg-bg-muted hover:text-fg']"
					:aria-pressed="wide"
					:aria-label="wide ? t('dock.narrow') : t('dock.wide')"
					@click="emit('toggleWide')"
				>
					<Icon :name="wide ? 'minimize' : 'maximize'" class="size-5" />
				</button>
			</Tooltip>
			<Tooltip v-if="!newInSidebar" :text="armed ? t('dock.confirmRestart') : t('dock.newConversation')">
				<button
					type="button"
					:class="[BUTTON, armed ? 'bg-danger-subtle text-danger' : 'text-fg-subtle hover:bg-bg-muted hover:text-fg']"
					:aria-label="armed ? t('dock.confirmRestart') : t('dock.newConversation')"
					@click="emit('startNew')"
				>
					<Icon :name="armed ? 'check' : 'compose'" class="size-5" />
				</button>
			</Tooltip>
			<button
				type="button"
				:class="[BUTTON, 'text-fg-subtle hover:bg-bg-muted hover:text-fg']"
				:aria-label="t('dock.close')"
				@click="emit('close')"
			>
				<Icon name="close" class="size-5" />
			</button>
		</div>
	</header>
</template>
